import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MDTagComponent } from '@melodic-ui/components';
import { ILessonRosterMember } from '@lb/api/mylb/interfaces';
import { UserStateService } from '@lb/state/user-state.service';
import { UserIconComponent } from '@lb/components';

/**
 * Roll-call checkboxes for a multi-attendance (chirp room) lesson.
 *
 * Starts with everyone UNCHECKED: the instructor checks who is present, and anyone left unchecked is
 * recorded Absent by the server (D6) — completeness is structural, there is no partial roster. After
 * attendance is taken the component renders read-only Present/Absent tags from the recorded rows.
 *
 * Member names resolve from cached User.Api summaries; the roster itself carries IDs only (OQ15).
 */
@Component({
	selector: 'lb-lesson-roster-attendance',
	standalone: true,
	imports: [MDTagComponent, UserIconComponent],
	templateUrl: './lesson-roster-attendance.component.html',
	styleUrl: './lesson-roster-attendance.component.scss'
})
export class LessonRosterAttendanceComponent {
	private readonly _userStateService = inject(UserStateService);

	members = input.required<ILessonRosterMember[]>();
	/** True once attendance is recorded — rows show tags instead of checkboxes. */
	readonly = input<boolean>(false);
	/** Inert-but-visible before the lesson's edit window opens, matching the rest of the form. */
	disabled = input<boolean>(false);

	presentChange = output<number[]>();
	/** True when the instructor has explicitly confirmed nobody attended (the "No attendees" row). */
	noAttendeesChange = output<boolean>();

	private readonly _presentIDs = signal<Set<number>>(new Set());
	private readonly _noAttendees = signal<boolean>(false);

	presentCount = computed(() => this._presentIDs().size);
	noAttendees = computed(() => this._noAttendees());

	constructor() {
		effect(() => {
			const userIDs = this.members().map((member) => member.userID);
			if (userIDs.length) {
				this._userStateService.loadUserSummaries(userIDs);
			}
		});
	}

	memberName(userID: number): string {
		return this._userStateService.getUserSummary(userID)()?.fullName ?? '';
	}

	isPresent(enrollmentMemberID: number): boolean {
		return this._presentIDs().has(enrollmentMemberID);
	}

	wasRecordedPresent(member: ILessonRosterMember): boolean {
		return member.attendanceStatusID === 1; // LessonAttendanceStatus.Present
	}

	toggle(enrollmentMemberID: number, checked: boolean): void {
		this._presentIDs.update((current) => {
			const next = new Set(current);
			if (checked) {
				next.add(enrollmentMemberID);
			} else {
				next.delete(enrollmentMemberID);
			}
			return next;
		});

		// A real attendee contradicts "No attendees" — clear it (the row also disables while anyone is checked).
		if (checked && this._noAttendees()) {
			this._noAttendees.set(false);
			this.noAttendeesChange.emit(false);
		}

		this.presentChange.emit([...this._presentIDs()]);
	}

	toggleNoAttendees(checked: boolean): void {
		this._noAttendees.set(checked);
		this.noAttendeesChange.emit(checked);
	}
}
